import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'

import { TrackListProps } from './TrackList.types'
import TrackRow from '../TrackRow'
import TrackRowDragImage from '../TrackRowDragImage'
import getAlbumArtwork from '../../../services/albumArtworkService'
import styles from './TrackList.module.css'
import { usePlayerApi } from '@audio-network/player'

const TrackList = ({
	onDragAndDrop,
	onDelete,
	onMoveToBottom,
	onMoveToTop,
	tracks,
	notes,
	onChangeNotes,
	source,
	showRowNumbers = false,
	areTracksSelected,
	setTrackSelected,
	handleChecked,
	shouldInitialiseQueue = true,
	...props
}: TrackListProps): JSX.Element => {
	const [albumArtwork, setAlbumArtwork] =
		useState<ContentfulAlbumArtwork[]>(null)
	const trackRowDragImageRef = useRef(null)
	const [currentDraggingTrack, setCurrentDraggingTrack] = useState<Track>(null)
	const trackListRef = useRef(null)
	const { initialiseQueue } = usePlayerApi()
	const [trackCount, setTrackCount] = useState(0)
	const isProcessing = useRef(false)

	const fetchAlbumArtwork = useCallback(async (): Promise<void> => {
		try {
			isProcessing.current = true

			// Get unique album numbers from tracks
			const uniqueAlbumNumbers = [
				...new Set(tracks.map((track) => track.album.number)),
			]

			// Determine which album artwork needs to be fetched
			const existingArtworkNumbers =
				albumArtwork?.map((artwork) => artwork.albumNumber) ?? []

			const newArtworkNumbers = uniqueAlbumNumbers.filter(
				(number) => !existingArtworkNumbers.includes(number)
			)

			// Only fetch if there are new albums
			if (newArtworkNumbers.length === 0) {
				return
			}

			const newArtwork = await getAlbumArtwork(newArtworkNumbers)

			setAlbumArtwork((prev) => (prev ? [...prev, ...newArtwork] : newArtwork))
		} finally {
			isProcessing.current = false
		}
	}, [albumArtwork, tracks])

	useEffect(() => {
		// Handle album artwork fetching
		const handleAlbumArtwork = (): void => {
			if (tracks.length !== trackCount && !isProcessing.current) {
				setTrackCount(tracks.length)
				fetchAlbumArtwork()
			}
		}

		// Handle queue initialization
		const handleQueueInit = (): void => {
			if (shouldInitialiseQueue) {
				initialiseQueue(tracks)
			}
		}

		handleAlbumArtwork()
		handleQueueInit()
	}, [
		fetchAlbumArtwork,
		tracks,
		initialiseQueue,
		shouldInitialiseQueue,
		trackCount,
	])

	const handleDragOver = (event: React.DragEvent): void => {
		event.preventDefault()

		const isModalOpen =
			document.getElementById('anw-modal-portal').dataset.modal === 'true'

		if (isModalOpen) {
			return
		}

		const dragTarget: HTMLElement = (event.target as Element).closest(
			'[draggable]'
		)

		if (!dragTarget) {
			return
		}

		const { top, bottom } = dragTarget.getBoundingClientRect()
		const { clientY } = event

		const goesAbove = clientY - top < bottom - clientY

		if (
			(goesAbove && dragTarget.dataset.dragmaker === 'above') ||
			(!goesAbove && dragTarget.dataset.dragmaker === 'below')
		) {
			return
		}

		dragTarget.dataset.dragmarker = goesAbove ? 'above' : 'below'
	}

	const handleDrop = useCallback(
		(event: React.DragEvent, index: number): void => {
			const isModalOpen =
				document.getElementById('anw-modal-portal').dataset.modal === 'true'

			if (isModalOpen) {
				return
			}

			const dragTarget: HTMLElement = (event.target as Element).closest(
				'[draggable]'
			)

			if (!dragTarget) {
				return
			}

			event.preventDefault()

			const draggableId = event.dataTransfer.getData('text/plain')
			const reordering = tracks.findIndex(
				(track) => track.id.toString() === draggableId
			)

			const goesAbove = dragTarget.dataset.dragmarker === 'above'

			const oldPosition = tracks.findIndex(
				(track) => track.id === parseInt(draggableId, 10)
			)

			let newPosition: number

			if (index === oldPosition) {
				newPosition === index
				return
			}

			if (index < oldPosition) {
				newPosition = goesAbove ? index : index + 1
			} else {
				newPosition = goesAbove ? index - 1 : index
			}

			const reorderedTracks = [...tracks]
			reorderedTracks.splice(
				newPosition,
				0,
				reorderedTracks.splice(reordering, 1)[0]
			)
			onDragAndDrop(oldPosition, newPosition, reorderedTracks)
		},
		[onDragAndDrop, tracks]
	)

	const handleDragStart = (event: React.DragEvent, track: Track): void => {
		const isModalOpen =
			document.getElementById('anw-modal-portal').dataset.modal === 'true'

		if (isModalOpen) {
			return
		}

		const dragElement: HTMLElement = (event.target as Element).closest(
			'[draggable]'
		)

		if (!dragElement) {
			return
		}

		event.dataTransfer.clearData()
		event.dataTransfer.setData('text/plain', track.id.toString())
		event.dataTransfer.effectAllowed = 'move'
		setCurrentDraggingTrack(track)
		event.dataTransfer.setDragImage(trackRowDragImageRef.current, -1, -1)
	}

	const handleDragEnter = (event: React.DragEvent): void => {
		event.preventDefault()
		event.dataTransfer.effectAllowed = 'move'
	}

	const handleDragLeave = (event: React.DragEvent): void => {
		const isModalOpen =
			document.getElementById('anw-modal-portal').dataset.modal === 'true'

		if (isModalOpen) {
			return
		}

		const dragTarget: HTMLElement = (event.target as Element).closest(
			'[draggable]'
		)

		if (
			!dragTarget ||
			(event.target as HTMLElement)?.closest('[draggable]') ===
				(event.relatedTarget as HTMLElement)?.closest('[draggable]')
		) {
			return
		}

		dragTarget.removeAttribute('data-dragmarker')
	}

	const handleDragEnd = (): void => {
		document.body
			.querySelectorAll('[data-dragmarker]')
			.forEach((element: HTMLElement) =>
				element.removeAttribute('data-dragmarker')
			)
		setCurrentDraggingTrack(null)
	}

	const renderListItems = useMemo(
		() => (
			<>
				{tracks.map((track, index) => (
					<li
						className={styles.trackListItem}
						key={track.id}
						data-testid="track-row"
					>
						<TrackRow
							rowNumber={showRowNumbers ? index + 1 : null}
							// TODO: This should just be part of track
							albumArtworkUrl={
								track.album.artworkUrl ??
								albumArtwork?.filter(
									(artwork) => artwork.albumNumber === track.album.number
								)[0]?.url
							}
							// TODO: We shouldn't need a marker for fetching (we should fetch SS)
							fetchingAlbumArtworkUrl={!albumArtwork}
							onDelete={onDelete}
							onMoveToBottom={onMoveToBottom}
							onMoveToTop={onMoveToTop}
							track={track}
							source={source}
							{...(onDragAndDrop && {
								draggable: true,
								onDragOver: handleDragOver,
								onDragStart: (event) => handleDragStart(event, track),
								onDragEnd: handleDragEnd,
								onDrop: (event) => handleDrop(event, index),
								onDragEnter: handleDragEnter,
								onDragLeave: handleDragLeave,
							})}
							notes={
								notes &&
								(notes.find((note) => note.Id === track.id)?.Notes ?? '')
							}
							onChangeNotes={onChangeNotes}
							showSelect={areTracksSelected}
							setTrackSelected={setTrackSelected}
							handleChecked={handleChecked}
						/>
					</li>
				))}
			</>
		),
		[
			albumArtwork,
			handleDrop,
			notes,
			onChangeNotes,
			onDelete,
			onDragAndDrop,
			onMoveToBottom,
			onMoveToTop,
			showRowNumbers,
			source,
			tracks,
			areTracksSelected,
			setTrackSelected,
			handleChecked,
		]
	)

	return (
		<ol {...props} ref={trackListRef} className={styles.trackList}>
			{renderListItems}
			<TrackRowDragImage
				track={currentDraggingTrack}
				ref={trackRowDragImageRef}
			/>
		</ol>
	)
}

export default memo(TrackList)
